import { z } from "zod";
import { environment } from "@/environment/environment";

const hexColorCodeSchema = z
  .string()
  .trim()
  .regex(/^#(?:[\da-f]{3,4}|[\da-f]{6,8})$/iu) // #000000ff, #000000, #000f, #000
  .transform((string) => string.toLocaleLowerCase());

const classLabelSchema = z.object({
  displayName: z.string().trim().min(1).nullish(),
  hexColorCode: hexColorCodeSchema.nullish(),
  id: z.string().trim().min(1),
  parentId: z.string().trim().min(1).nullish(),
});

export type ClassLabelElement = z.infer<typeof classLabelSchema>;

const nameSchema = z.string().trim();

export const AppConfigSchema = z.object({
  apiRefetchInterval: z.number().default(5000),
  apiUrl: z.string().trim().default(environment.NEXT_PUBLIC_API_URL),
  classLabelList: z.array(classLabelSchema).optional(),
  colorTree: z.record(z.string().trim(), hexColorCodeSchema.nullish()).optional(),
  defaultFeedConfigurationList: z.array(z.tuple([nameSchema, nameSchema.nullable()])).default([]),
  feedbackRecording: z
    .object({
      checkoutDuration: z.number().min(1000).max(300_000).default(30_000),
      isDisabled: z.boolean().default(false),
      quality: z.number().min(0.1).max(1).default(0.5),
      sampleRate: z.number().min(1).max(50).default(15),
    })
    .optional(),
  feedBearingTimeConfigurationNameAndModelNamePresetList: z
    .array(z.tuple([nameSchema, nameSchema.nullable()]))
    .default([]),
  isDefaultConfig: z.boolean().default(false),
  nearestNeighborSmoothingRadius: z.number().default(2),
  pinnedBearingTimeConfigurationNameList: nameSchema.array().default([]),
  pinnedModelNameList: nameSchema.array().default([]),
  shouldCache: z.boolean().default(true),
  shouldRememberTourSettings: z.boolean().default(true),
  shouldSmoothModelLayerByDefault: z.boolean().default(true),
  tileAgeBeforeCacheInSeconds: z.number().default(180),
});

export type AppConfig = z.infer<typeof AppConfigSchema>;

export const AppConfigDefault = AppConfigSchema.parse({
  isDefaultConfig: true,
});
